import { Box, List, ListItem, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { member as v_member, basic, Values, product as v_product, SNACKBAR_TIME, FINISH_TIME } from '../../../request/values';
import { COLORS } from '../../../shared/Theme';
import { DialogConfirm } from '../../../components/Dialog';
import ToastStr from '../../../request/toastStr';
import { TitleBarWithButton } from '../../../shared/header/TitleBar';
import { getTranslatedLicenseName, formatMoneyAmount } from '../../../locales/formatModule';

const useStyles = makeStyles((theme: Theme) => ({
	container: {
		padding: 0,
		height: '88vh',
		width: '100vw',
		paddingTop: '3.5vh',
		background: `${COLORS.WHITE}`,
	},
	productItem: {
		borderRadius: 5,
		border: 'solid 1px #d3d3d3',
		margin: 'auto',
		marginBottom: '1.5vh',

		width: '90vw',
		padding: '20px',

		'& #itemWrap': {
			width: '100%',
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			alignItems: 'center',
			gap: '15px',
			'& #itemImage': {
				flex: 1,
				'& #itemImageSize': {
					width: '100%',
				},
			},
			'& #itemDesc': {
				flex: 2,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '10px',
				'& #itemInfo': {
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'space-between',
					width: '100%',
					fontSize: '14px',
					'& #itemName': {
						backgroundColor: `${COLORS.WATER_BLUE}`,
						color: `${COLORS.WHITE}`,
						padding: '5px 10px',
						borderRadius: '5px',
					},
					'& #itemLevel': {
						backgroundColor: `${COLORS.WATER_BLUE}`,
						color: `${COLORS.WHITE}`,
						padding: '5px 10px',
						borderRadius: '5px',
					},
				},
				'& #productInfo': {
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
					gap: '5px',
					fontSize: '12px',
				},
				'& #productPrice': {
					width: '100%',
					color: `${COLORS.WATER_BLUE}`,
					margin: '10px 0',
					fontSize: '16px',
					fontWeight: 'bold',
				},
				'& #productButton': {
					width: '100%',
					backgroundColor: `${COLORS.WATER_BLUE}`,
					color: `${COLORS.WHITE}`,
					borderRadius: '10px',
					border: 'none',
					padding: '10px',
					fontSize: '16px',
					lineHeight: '20px',
					fontWeight: 'bold',
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '5px',
					'& #productButtonIcon': {
						width: '20px',
						height: '20px',
					},
				},
			},
		},
		[theme.breakpoints.up('md')]: {
			width: '60vw',
			padding: '30px',
		},
	},

	list: {
		width: '95vw',
		height: '74vh',
		overflowY: 'auto',
		...theme.typography.subtitle1,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		[theme.breakpoints.up('md')]: {
			width: '62vw',
			height: '100%',
			paddingTop: theme.spacing(1),
			paddingBottom: theme.spacing(6),
		},
	},
	empty: {
		height: '62vh',
		textAlign: 'center',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& img': {
			width: '35vw',
			height: '35vw',
			[theme.breakpoints.up('md')]: {
				width: '25vw',
				height: '25vw',
			},
			marginBottom: '2.5vh',
			opacity: 0.4,
		},
		'& h1': {
			color: `${COLORS.DOWN}`,
		},
	},
	buttonWrapper: {
		textAlign: 'right',
		marginLeft: 'auto',
		paddingBottom: '1vh',
		paddingRight: '6vw',
		[theme.breakpoints.up('md')]: {
			paddingRight: '10px',
		},
	},
	button: {
		[theme.breakpoints.up('md')]: {
			padding: '1vw',
		},
		padding: '2vw',
		height: 'auto',
		borderRadius: '5px',
		backgroundColor: `${COLORS.WATER_BLUE}`,
		color: `${COLORS.WHITE}`,
		boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
		...theme.typography.body1,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		width: 'auto',
	},
}));

interface iproductList {
	productName: string;
	productNum: string;
	productImage: string;
	productPrice: number;
	quantity: number;
	companyName: string;
	companyNum: number;
	memberName: string;
	productLicenseLevel: number;
	productSaleAppNum: number;
	productSaleAppDate: string;
	productListPrice: number;
}

export interface refHandle {
	refreshCartList: () => void;
	handleOpen: () => void;
}

type iDialog = {
	open: boolean;
	type: string;
	index: number;
};

const ProductPurchase: React.FC<any> = (props) => {
	const classes = useStyles();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const history = useHistory();
	const { member } = props;
	const { t } = useTranslation('student');

	const count = 20;
	let max_count: number = 999;
	const loader = useRef(null);

	const [product, setProduct] = useState<iproductList[]>([]);
	const [page, setPage] = useState(-count);
	const [loading, setLoading] = useState(false);
	const [firstCheck, setFirstCheck] = useState(false);

	const [dialog, setDialog] = useState<iDialog>({
		open: false,
		type: 'approval',
		index: 0,
	});

	const handleObserver = useCallback((entries: any) => {
		const target = entries[0];
		if (target.isIntersecting) {
			setPage((prevState) => {
				if (prevState + count > max_count) {
					return prevState;
				}
				return prevState + count;
			});
		}
	}, []);

	const handleClose = () => {
		console.log('click close');
		setDialog((prevState: any) => {
			return Object.assign({}, prevState, { open: false });
		});
	};

	const handleApprovalOpen = (index: number) => {
		console.log(index);
		if (index >= 0) {
			setDialog((prevState: any) => {
				return Object.assign({}, prevState, {
					open: true,
					type: 'approval',
					index: index,
				});
			});
		}
	};

	const moveProductOrderList = () => {
		history.push({
			pathname: '/ProductOrderList',
		});
	};

	const postProductPurchase = (product: any) => {
		let url = new URL(basic.server + v_member.member + v_product.purchase_application);
		axios({
			method: 'POST',
			data: {
				companyNum: member.companyNum,
				product: product,
			},
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				// console.log(data)
				if (data.code === Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_PURCHASE_SUCCESS, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
					setProduct((prevState) => {
						prevState.splice(dialog.index, 1);
						return prevState.slice();
					});
				} else if (data.code === Values.SALE_FINISH_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_FINISH, {
						variant: 'warning',
						autoHideDuration: FINISH_TIME,
					});
				} else if (data.code === Values.SALE_COMPANY_UPDATE_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_COMPANY_UPDATE_STR, {
						variant: 'warning',
						autoHideDuration: FINISH_TIME,
					});
				} else if (data.code === Values.INSUFFICIENT_CODE) {
					enqueueSnackbar(ToastStr.COMPANY_STOCK_INSUFFICIENT_STR, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code === Values.FAIL_CODE) {
					if (data.msg)
						enqueueSnackbar(data.msg, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					else
						enqueueSnackbar(ToastStr.PRODUCT_PURCHASE_FAIL, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			});

		handleClose();
	};

	async function getMaxProductCount(): Promise<number> {
		let url = new URL(basic.server + v_member.member + v_product.purchase_list);
		let params = {
			classCode: member.classCode,
			companyNum: member.companyNum,
		};
		url.search = new URLSearchParams(params).toString();

		const response = await axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		});
		const data = await response.data;
		const count: number = data.count;
		return count;
	}

	const getProductPurchaseAppList = (position: number, count: number) => {
		let url = new URL(basic.server + v_member.member + v_product.purchase_app_list);
		let params = {
			classCode: member.classCode,
			companyNum: member.companyNum,
			position: position.toString(),
			count: count.toString(),
			keyword: '',
		};
		url.search = new URLSearchParams(params).toString();

		axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);
				if (data.code === Values.SUCCESS_CODE) {
					setLoading(true);
					if (product.length === 0 && data.products.length === 0) {
						setFirstCheck((prev: boolean) => {
							if (!prev) {
								enqueueSnackbar(ToastStr.PRODUCT_APP_LIST_NULL_STR, {
									variant: 'success',
									autoHideDuration: SNACKBAR_TIME,
								});
							}
							return true;
						});
					} else if (data.products === null) {
						enqueueSnackbar(ToastStr.PRODUCT_APP_LIST_FAIL_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else {
						if (product.length === 0) {
							setProduct((prevState) => data.products);
						} else {
							setProduct((prevState) => prevState.concat(data.products));
						}
					}
				} else if (data.code === Values.FAIL_CODE) {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code === Values.EXCEPTION_CODE) {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			});
	};

	useEffect(() => {
		const option = {
			root: null,
			rootMargin: '20px',
			threshold: 0,
		};
		const observer = new IntersectionObserver(handleObserver, option);
		if (loader.current) observer.observe(loader.current!);
	}, [handleObserver]);

	useEffect(() => {
		if (page >= 0) {
			getProductPurchaseAppList(page, count);
			const tick = setInterval(() => {
				getProductPurchaseAppList(page, count);
			}, 1000 * 5);
			return () => clearTimeout(tick);
		}
	}, [page]);

	useEffect(() => {
		const init = async () => {
			max_count = await getMaxProductCount();
		};
		init();
	}, []);

	return (
		<div className="contentSmallPdTop" data-testid="dist-product-purchase-dashboard">
			<TitleBarWithButton
				history={history}
				title={t('ProductPurchase.pageTitle')}
				titleIcon="cart"
				buttonCheck={true}
				buttonText={t('ProductPurchase.receiptButton')}
				buttonOnClick={moveProductOrderList}
			/>

			<Box display="grid" justifyContent="center" alignItems="center">
				<List className={classes.list}>
					{product.length != 0 ? (
						product.map((item: any, index) => {
							return (
								<ListItem className={classes.productItem} alignItems="center">
									<ProductItem index={index} {...item} approval={() => handleApprovalOpen(index)} member={member} />
								</ListItem>
							);
						})
					) : (
						<div className={classes.empty}>
							<div>
								<img src="/images/title/cart.webp" />
								<Typography variant="h1">{t('ProductPurchase.empty')}</Typography>
							</div>
						</div>
					)}
					<div ref={loader} />
				</List>
			</Box>

			<DialogConfirm
				open={dialog.open}
				handleClose={() => handleClose()}
				title={t('ProductPurchase.pageTitle')}
				context={t('ProductPurchase.confirmTitle')}
				yesConfirm={(e: any) => postProductPurchase(product[dialog.index])}
				noConfirm={() => handleClose()}
			/>
		</div>
	);
};

const ProductItem: React.FC<any> = (props) => {
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');
	const productName = getTranslatedLicenseName(tObject, props.productName);

	const { index, approval } = props;

	return (
		<div id="itemWrap">
			<div id="itemImage">
				<img id="itemImageSize" src={basic.server + '/displayImage?fileName=' + props.productImage} />
			</div>
			<div id="itemDesc">
				<div id="itemInfo">
					<div id="itemName">{productName}</div>
					<div id="itemLevel">{'Lev. ' + props.productLicenseLevel}</div>
				</div>
				<div id="productInfo">
					<div id="productCompanyName">{t('ProductPurchase.manufacturerPrefix') + props.companyName}</div>
					{(props.member.classVersion === 4 || props.member.classVersion === 5) && (
						<div id="productListPrice">{t('ProductPurchase.listPricePrefix') + formatMoneyAmount(props.productListPrice)}</div>
					)}
				</div>
				<div id="productPrice" data-testid={`dist-product-purchase-price-${index}`}>
					{t('ProductPurchase.salePricePrefix') + formatMoneyAmount(props.productPrice)}
				</div>
				<button id="productButton" onClick={approval} data-testid={`dist-product-purchase-approve-btn-${index}`}>
					<div>{t('ProductPurchase.purchaseButton')}</div>
					<img id="productButtonIcon" src="/images/cursor_click_white.svg" />
				</button>
			</div>
		</div>
	);
};

export default ProductPurchase;
